import { EventEntityItem } from 'components/eventView/pages/eventData';
import { MarkdownContent } from 'elements/visual/MarkdownContent';
import { EColors2, EFonts } from 'libs/styles/styling';
import { getSmallerImage } from 'libs/utils/imageUtils/imageUtils';
import { useAnalytics } from 'libs/utils/site/analytics';
import { useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CtxEventView } from 'session/ctxEventView';
import styled, { css } from 'styled-components/macro';

interface Props {
    className?: string;
    entity: EventEntityItem;
    mobilePathTo: string;
    showDescription?: boolean;
}

export const EntityListItem = (props: Props) => {
    const ctxEventView = useContext(CtxEventView);
    const entity = props.entity;

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const analytics = useAnalytics();
    const isWide = ctxEventView.isWide;

    function handleClick() {
        analytics.track('event_ui.detail.open_clicked', {
            type: 'entity',
            storyId: entity.storyId,
            title: ctxEventView.allStories.find((e) => e.storyId === entity.storyId)?.title
        });

        if (!isWide) {
            const mobilePath = `${props.mobilePathTo}/${entity.storyId}`;
            navigate(mobilePath);
        } else {
            searchParams.set('detailId', entity.storyId);
            searchParams.set('markerId', entity.markerId);
            setSearchParams(searchParams);
        }
    }

    function getFontSize(text: string) {
        if (isWide) {
            if (text.length > 90) {
                return 12;
            } else if (text.length > 80) {
                return 13;
            } else if (text.length > 70) {
                return 14;
            } else if (text.length > 50) {
                return 15;
            } else {
                return 16;
            }
        } else {
            if (text.length > 90) {
                return 11;
            } else if (text.length > 80) {
                return 12;
            } else if (text.length > 70) {
                return 13;
            } else if (text.length > 50) {
                return 14;
            } else {
                return 15;
            }
        }
    }

    return (
        <S_EntityListItem
            className={props.className}
            $isHighlighted={entity.isHighlighted}
            onClick={handleClick}
            $isWide={isWide}
            $imageStyleId={entity.storyHeader?.imageStyleId}
        >
            <S_ImageContainer $isWide={isWide} $imageStyleId={entity.storyHeader?.imageStyleId}>
                <S_Image
                    $isWide={isWide}
                    $imageStyleId={entity.storyHeader?.imageStyleId}
                    $backgroundImageUrl={getSmallerImage(entity.imageUrl, 400)}
                />
            </S_ImageContainer>

            <S_Content $isWide={isWide} $imageStyleId={entity.storyHeader?.imageStyleId}>
                <S_Title $size={getFontSize(entity.title)}>{entity.title}</S_Title>
                {props.showDescription && <S_DetailDescription text={entity.storyHeader?.text} />}
            </S_Content>
            {/* {props.isHighlighted && (
                <S_InfoIconContainer>
                    <InfoCircle
                        icon={{
                            iconPrefix: 'fal',
                            iconName: 'star'
                        }}
                        bgColor={ctxEventView.eventData.eventInfo.styling.circle.bgColor}
                        iconColor={ctxEventView.eventData.eventInfo.styling.circle.iconColor}
                    />
                </S_InfoIconContainer>
            )} */}
        </S_EntityListItem>
    );
};

const S_EntityListItem = styled.div<{
    $isHighlighted: boolean;
    $isWide: boolean;
    $imageStyleId: string;
}>`
    display: flex;
    position: relative;
    flex-direction: column;
    border-radius: 15px;
    background: #ffffff;
    box-shadow: 5px 5px 15px 0px rgba(0, 0, 0, 0.01);
    min-height: 160px;
    &:hover {
        cursor: pointer;
        transform: scale(1.05);
    }
    ${(props) => {
        if (props.$isWide) {
            return css`
                height: unset;
            `;
        }
    }}
`;

const S_ImageContainer = styled.div<{ $isWide: boolean; $imageStyleId: string }>`
    width: 100%;
    flex-shrink: 0;
    ${(props) => {
        if (props.$isWide) {
            return css`
                height: 130px;
                padding: 15px 15px 0 15px;
            `;
        } else {
            if (props.$imageStyleId === 'stretch') {
                return css`
                    padding: 2px 2px 0 2px;
                    min-height: 90px;
                    border-top-right-radius: 15px;
                    border-top-left-radius: 15px;
                    overflow: hidden;
                `;
            } else {
                return css`
                    padding: 10px 10px 0 10px;
                    min-height: 75px;
                `;
            }
        }
    }}
`;

const S_Image = styled.div<{
    $isWide: boolean;
    $backgroundImageUrl: string;
    $imageStyleId: string;
}>`
    background-image: url('${(props) => props.$backgroundImageUrl}');
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    width: 100%;
    height: 100%;
    ${(props) => {
        if (props.$isWide) {
            if (props.$imageStyleId === 'stretch') {
                return css`
                    background-size: cover;
                    border-radius: 5px;
                `;
            }
        } else {
            if (props.$imageStyleId === 'stretch') {
                return css`
                    background-size: cover;
                `;
            }
        }
    }}
`;
const S_Content = styled.div<{ $isWide: boolean; $imageStyleId: string }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    text-align: center;
    justify-content: center;
    overflow: hidden;
    padding: 5px 10px 5px 10px;
    align-items: center;
    min-height: 87px;
    flex-shrink: 0;
    ${(props) => {
        if (props.$isWide) {
            return css`
                min-height: 87px;
            `;
        } else {
            if (props.$imageStyleId === 'strech') {
                return css`
                    min-height: 87px;
                `;
            } else {
                return css`
                    min-height: 72px;
                `;
            }
        }
    }}
`;
const S_Title = styled.div<{ $size: number }>`
    color: ${EColors2.DARK};
    font-family: ${EFonts.Medium};
    font-size: ${(props) => props.$size}px;
    width: 100%;
    display: flex;
    overflow: hidden;
    align-items: center;
    justify-content: center;
`;

const S_DetailDescription = styled(MarkdownContent)`
    color: #4a4a4a;
    font-family: ${EFonts.Regular};
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    // BEIN Ellipsis
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    // END Ellipsis
    line-height: 22px;
`;
